<template>
    <InputNumber
        v-model="value"
        suffix=" %"
        :name="name"
        :showButtons="showButtons"
        buttonLayout="horizontal"
        :inputStyle="{ 'text-align': 'right' }"
        :class="classes"
        incrementButtonIcon="pi pi-plus"
        decrementButtonIcon="pi pi-minus"
        :min="min"
        :max="max"
        :maxFractionDigits="maxFractionDigits"
        :minFractionDigits="minFractionDigits"
        :step="step"
        :locale="this.numberFormat()"
        :placeholder="$t('§ui.placeholder_input_percentage')"
        :disabled="disabled"
        @focus="onFocus()"
        @blur="onBlur()"
        @input="onInput"
    />
</template>

<script>
import InputNumber from 'primevue/inputnumber'

export default {
    name: 'CorePercentageInput',

    components: { InputNumber },

    emits: ['update:modelValue', 'input', 'focus', 'blur'],

    props: {
        modelValue: {
            type: Number,
            default: null,
        },
        name: {
            type: String,
            default: '',
        },
        inline: {
            type: Boolean,
            default: false,
        },
        min: {
            type: Number,
            default: null,
        },
        max: {
            type: Number,
            default: null,
        },
        maxFractionDigits: {
            type: Number,
            default: 0,
        },
        minFractionDigits: {
            type: Number,
            default: 0,
        },
        step: {
            type: Number,
            default: 1,
        },
        showButtons: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        validator: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        condensed: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            isFocused: false,
        }
    },

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue === '') {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        value: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        classes() {
            return {
                'core-percentage-input': true,
                'core-percentage-input--inline': this.inline,
                'core-valid': this.validator && !this.validator.$invalid && this.validator.$dirty,
                'core-invalid': this.validator && this.validator.$invalid && this.validator.$dirty,
                'core-warning': false,
            }
        },

        validatorErrors() {
            if (this.validator?.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator?.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },
    },

    methods: {
        onFocus() {
            this.isFocused = true
            this.$emit('focus')
        },

        onBlur() {
            this.isFocused = false
            this.$emit('blur')
        },

        // PrimeVue updates the model only on blur, Enter/Tab and spin; this fires on every keystroke
        onInput(event) {
            this.$emit('input', event.value)
        },

        numberFormat() {
            return navigator.language || 'en-US'
        },
    },
}
</script>

<style>
.core-percentage-input {
    & input {
        font-size: var(--text-base);
        width: 100%;
    }

    & > input:disabled {
        background-color: var(--color-disabled-bg);
        color: var(--text-disabled);
    }

    &.core-valid input,
    &.core-valid input:focus,
    &.core-valid input:hover {
        border-color: var(--color-success);
    }

    &.core-invalid input,
    &.core-invalid input:focus,
    &.core-invalid input:hover {
        border-color: var(--color-error);
    }

    &.core-warning input,
    &.core-warning input:focus,
    &.core-warning input:hover {
        border-color: var(--color-warning);
    }
}
</style>
